import _ from 'lodash';
import { UserType } from './UserType'
import { NotificationType } from './NotificationType'
import { ParticipationLevel } from './ParticipantLevel'
import { hasValue } from '../../../../services/util/CommonUtil'
import { trackEvent, HeapEvents } from '../../../../services/util/AnalyticsUtil'
import MessageApi from 'services/api/MessageApi';
import ticketsApi from 'services/api/ticketsApi';

/* Service class used to perform api calls for messaging */
class MessageService {

    constructor() {
        this.api = MessageApi.create();
        this.ticketsApi = ticketsApi.create();
    }

    /**
     * Retrieves the memberships for the specified conversation and checks the current user and vdo client ids against it.
     * If memberships don't exist for these two ids, it will create them.
     * @param {any} taxProId
     * @param {any} clientId
     * @param {any} conversationId
     */
    async checkConversationMemberships(taxProId, clientId, conversationId) {
        let success = true

        let memberships = (await this.getMemberships(conversationId)) || []

        // check/create membership for the tax pro
        let proMembership = memberships.find(membership =>
            membership.participant && (membership.participant.userId === taxProId))
        if (!proMembership) {
            let proMembershipId = await this.createMembership(conversationId, null, null, NotificationType.DEFAULT, ParticipationLevel.WRITE, false)
            success = success && hasValue(proMembershipId)
        }

        // check/create membership for the client
        let clientMembership = memberships.find(membership =>
            membership.participant && (membership.participant.userId === clientId && membership.participant.userType === UserType.ONLINE_IDENTITY_SERVICE))       

        return success
    }

    /**
     * Retrieves a conversation for the given user id and type with the specified tags.
     * @param {any} userId
     * @param {any} userType
     * @param {any} product
     * @param {any} offset
     */
    async getConversation(userId, userType, product, offset) {
        let conversationId = -1 // BAD CONVERSATION! BAD!
        try {

            offset = hasValue(offset) ? offset : 0

            let response = await this.api.getConversation(userId, userType, product, offset);
            if (response && response.status === 200) {
                if (response.data.count > 0) {
                    conversationId = response.data.items[0].id
                }
            }
        } catch (ex) {
            console.log(ex)
        }

        return conversationId
    }

    /**
     * Creates a conversation using the provided name and product to assign it a friendly name.
     * @param {any} name
     * @param {any} product
     */
    async createConversation(name, product) {
        let conversationId = null
        try {
            let payload = {
                name: `${name} - ${product}`
            }
            let response = await this.api.createConversation(payload);
            if (response && response.status === 201) {
                conversationId = response.data.id
            }
        } catch (ex) {
            console.log(ex)
        }

        return conversationId
    }

    /**
     * Adds a tag to an existing conversation
     * @param {any} conversationId
     * @param {any} tag
     */
    async createConversationTag(conversationId, tag) {
        let success = false

        try {
            let payload = {
                conversationId: conversationId,
                name: tag
            }
            let response = await this.api.createConversationTag(payload);
            if (response && response.status === 201) {
                success = true
            }   
        } catch (ex) {
            console.log(ex)
        }

        return success
    }

    /**
     * Retrieves the membership list for the given conversation
     * @param {any} conversationId
     */
    async getMemberships(conversationId) {
        let memberships = null;

        // check for invalid conversation
        if (!hasValue(conversationId) || conversationId === -1) {
            return memberships
        }

        try {
            let response = await this.api.getMemberships(conversationId);
            if (response && response.status === 200) {
                if (response.data.count > 0) {
                    memberships = response.data.items
                }
            }
        } catch (ex) {
            console.log(ex)
        }

        return memberships
    }

    /**
     * Adds the specified user as a member of the given conversation
     * @param {any} conversationId
     * @param {any} userId
     * @param {any} userType
     * @param {any} notificationType
     * @param {any} participationLevel
     */
    async createMembership(conversationId, userId, userType, notificationType, participationLevel, isOwner) {
        let membershipId = null

        // check for invalid conversation
        if (!hasValue(conversationId) || conversationId === -1) {
            return membershipId
        }

        try {
            let payload = {
                conversationId: conversationId,
                participant: (!userId || !userType) ? null : {
                    userId: userId,
                    userType: userType
                },
                conversationStartIndex: null,
                conversationEndIndex: null,
                notificationType: notificationType,
                participationLevel: participationLevel,
                isOwner: isOwner
            }

            let response = await this.api.createMembership(payload);
            if (response && response.status === 201) {
                membershipId = response.data.id
            }
        } catch (ex) {
            console.log(ex)
        }

        return membershipId
    }

    /**
     * Retrieves the messages for this conversation for this user.
     * @param {any} conversationId
     * @param {any} offset
     * @param {any} limit
     */
    async getMessages(conversationId, offset, limit) {
        let messages = [];
        if (!offset) offset = 0
        if (!limit) limit = 25

        // check for invalid conversation
        if (!hasValue(conversationId) || conversationId === -1) {
            return messages
        }
      
        try {
            let response = await this.api.getMessages(conversationId, offset, limit);
            if (response && response.status === 200) {
                messages = response.data
            }
        } catch (ex) {
            console.log(ex)
        }

        return messages
    }

    /**
     * Creates a new message in the given conversation 
     * @param {any} conversationId
     * @param {any} messageContent
     * @param {any} ticketId
     */
    async createMessage(conversationId, messageContent, ticketId) {
        let message = null

        // check for invalid conversation
        if (!hasValue(conversationId) || conversationId === -1) {
            return message
        }

        try {
            let payload = {
                conversationId: conversationId,
                messageContent: messageContent
            }

            let response = await this.api.createMessage(payload);
            if (response && response.status === 201) {
                try {
                    trackEvent(HeapEvents.SEND_MESSAGE, ticketId);
                    await this.ticketsApi.touchTicket(ticketId);
                } catch (x) {
                    console.log(x)
                }
                message = response.data
            }
        } catch (ex) {
            console.log(ex)
        }

        return message
    }

    /**
     * Gets the number of unread messages in the given conversation 
     * @param {any} conversationId
     */
    async getUnreadMessagesCount(conversationId) {
        let count = 0

        // check for invalid conversation
        if (!hasValue(conversationId) || conversationId === -1) {
            return count
        }

        try {            
            let response = await this.api.getUnreadMessagesCount(conversationId);
            if (response && response.status === 200) {
                count = response.data
            }
        } catch (ex) {
            console.log(ex)
        }

        return count
    }

    /**
     * performs a post to the server with an array of message ids to mark as read
     * @param {any} ids
     */
    async markMessagesRead(ids) {
        var maxMessageId = (ids || []).reduce((a, b) => Math.max(a, b));

        let status = false
        if (maxMessageId) {
            try {

                let response = await this.api.markMessagesRead(maxMessageId);
                if (response && response.status === 200) {
                    status = response.data
                }
            } catch (ex) {
                console.log(ex)
            }
        }

        return { maxMessageId: maxMessageId, status }
    }

    /**
     * method to filter out duplicate messages and sort them in ascending order based on their creationTime
     * @param {any} messages
     */
    filterAndSort(messages) {
        let uniqueArray = _.uniqBy(messages, 'id') // remove dupes
        // hack: hackity... sent messages (for some reason) have the 'Z' appended to the date, while the rest don't. 
        //       For now, make them uniform, so the sorting actually works!
        return _.sortBy(uniqueArray, m => new Date(m.creationTime.endsWith('Z') ? m.creationTime.slice(0, -1) : m.creationTime))
    }

    /**
     * method to pull out the ids of all unread messages in a given array
     * @param {any} messages
     */
    extractUnreadMessageIds(messages) {
        // find all unread messages and their ids
        let unreadMessages = _.remove([...messages], msg => !msg.isRead)
        let unreadIds = unreadMessages.map(msg => msg.id)
        return unreadIds
    }
}

const messageService = new MessageService();
export default messageService;